import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getSubscriptionStatus, SubscriptionStatusResponse, UsageDetail } from '../api/client';
import CelestialLayout from '../components/celestial/CelestialLayout';
import TopNav from '../components/celestial/TopNav';
import SiteFooter from '../components/celestial/SiteFooter';
import MaterialIcon from '../components/celestial/MaterialIcon';

function getStatusLabel(status: SubscriptionStatusResponse['subscription']): string {
  if (status.is_active) return 'Aktif';
  const s = (status.status || '').toLowerCase();
  if (s === 'active') return 'Aktif';
  if (s === 'inactive') return 'Pasif';
  if (s === 'cancelled') return 'İptal edildi';
  if (s === 'expired') return 'Süresi doldu';
  return s || '—';
}

function formatUsageRow(detail: UsageDetail): string {
  const used = detail.used ?? 0;
  const limit = detail.limit ?? 0;
  const remaining = detail.remaining ?? 0;
  if (limit < 0) return `${used} kullanıldı (sınırsız)`;
  return `${used} / ${limit} (${remaining} kalan)`;
}

export default function Profile() {
  const { user, loading: authLoading, logout } = useAuth();
  const [status, setStatus] = useState<SubscriptionStatusResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) {
      navigate('/giris');
      return;
    }
    let cancelled = false;
    getSubscriptionStatus()
      .then((data) => { if (!cancelled) setStatus(data); })
      .catch((e) => { if (!cancelled) setError(e instanceof Error ? e.message : 'Yüklenemedi'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [user, navigate]);

  if (authLoading || !user) return null;

  const planLabel = status?.subscription.tier === 'plus' ? 'Astro Plus' : 'Ücretsiz';
  const usage = status?.usage;
  const statusLabel = status ? getStatusLabel(status.subscription) : '—';
  const isPlus = status?.subscription.tier === 'plus';

  return (
    <CelestialLayout>
      <TopNav />
      <main className="celestial-main celestial-section-pad">
        <div className="celestial-container">
          <div className="celestial-profile-header">
            <h1 className="celestial-headline-xl">Profil</h1>
            <p className="celestial-body-lg">{user.email}</p>
            {user.name && (
              <p className="celestial-body-lg" style={{ fontSize: '1rem', marginTop: '0.25rem' }}>
                {user.name}
              </p>
            )}
            <button
              type="button"
              className="celestial-btn-ghost"
              style={{ marginTop: '1rem' }}
              onClick={() => { logout(); navigate('/'); }}
            >
              <MaterialIcon name="logout" size={18} />
              Çıkış yap
            </button>
          </div>

          {error && <div className="celestial-error">{error}</div>}
          {loading && (
            <p className="celestial-body-lg">Yükleniyor…</p>
          )}

          {status && !loading && (
            <div className="celestial-bento">
              <article className="celestial-glass celestial-bento__card">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem' }}>
                  <h2 className="celestial-headline-lg">Sürüm</h2>
                  <MaterialIcon name="verified" />
                </div>
                <span className={isPlus ? 'celestial-badge-plus' : 'celestial-badge-free'}>
                  {isPlus && <MaterialIcon name="stars" size={18} />}
                  {planLabel}
                </span>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginTop: '1.5rem',
                    paddingTop: '1rem',
                    borderTop: '1px solid rgba(71, 69, 84, 0.2)',
                  }}
                >
                  <span className="celestial-body-lg" style={{ fontSize: '0.95rem' }}>Durum</span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--primary)' }}>
                    <span className="celestial-status-dot" aria-hidden />
                    {statusLabel}
                  </span>
                </div>
                {status.subscription.expires_at && isPlus && (
                  <p className="celestial-caption" style={{ textTransform: 'none', marginTop: '0.75rem' }}>
                    Bitiş: {new Date(status.subscription.expires_at).toLocaleDateString('tr-TR')}
                  </p>
                )}
              </article>

              {usage && (
                <article className="celestial-glass celestial-bento__card celestial-bento__usage">
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                    <h2 className="celestial-headline-lg">Kullanım özeti</h2>
                    <MaterialIcon name="monitoring" />
                  </div>
                  <p className="celestial-caption" style={{ textTransform: 'none', marginBottom: '1.5rem', paddingBottom: '1rem', borderBottom: '1px solid rgba(71,69,84,0.15)' }}>
                    Limitler her ay ve her gün yenilenir.
                  </p>
                  <div className="celestial-usage-row">
                    <div className="celestial-usage-row__left">
                      <div className="celestial-icon-box" style={{ width: 40, height: 40, marginBottom: 0 }}>
                        <MaterialIcon name="auto_awesome" size={22} />
                      </div>
                      <span className="celestial-usage-row__label">Aylık burç haritası</span>
                    </div>
                    <span className="celestial-usage-row__value">
                      {formatUsageRow(usage.monthly_astro_charts)}
                    </span>
                  </div>
                  <div className="celestial-usage-row">
                    <div className="celestial-usage-row__left">
                      <div className="celestial-icon-box" style={{ width: 40, height: 40, marginBottom: 0 }}>
                        <MaterialIcon name="style" size={22} />
                      </div>
                      <span className="celestial-usage-row__label">Günlük tarot okuması</span>
                    </div>
                    <span className="celestial-usage-row__value">
                      {formatUsageRow(usage.daily_tarot_readings)}
                    </span>
                  </div>
                </article>
              )}

              <article className="celestial-glass celestial-bento__card">
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1rem' }}>
                  <h2 className="celestial-headline-lg">Hesap silme</h2>
                  <MaterialIcon name="delete_forever" />
                </div>
                <p className="celestial-body-lg" style={{ fontSize: '0.9rem' }}>
                  Hesabınızı ve verilerinizi kalıcı olarak silmek için{' '}
                  <Link to="/hesap-sil">hesap silme sayfasını</Link> ziyaret edebilir veya uygulama
                  içinde Profil → Hesabı sil seçeneğini kullanabilirsiniz.
                </p>
                <Link to="/hesap-sil" className="celestial-btn-ghost" style={{ marginTop: '1rem' }}>
                  Hesap silme talimatları
                  <MaterialIcon name="arrow_forward" size={18} />
                </Link>
              </article>
            </div>
          )}

          <p style={{ marginTop: '2.5rem' }}>
            <Link to="/" className="celestial-btn-ghost">
              <MaterialIcon name="arrow_back" size={18} />
              Ana sayfaya dön
            </Link>
          </p>
        </div>
      </main>
      <SiteFooter />
    </CelestialLayout>
  );
}
